import React, { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { Controller, useFormContext } from 'react-hook-form';

import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
} from '@brand/components';
import {
  IDateFieldSchema,
  IFieldProps,
  IFieldStyles,
} from 'schema/generator/FormSchema';

import { useErrorMessage } from 'hooks/form/useErrorMessage';
import { useStyles } from 'hooks/form/useStyles';

import ComponentIsVisible from 'components/utils/IsVisible';

const DateField = ({
  field,
  id,
  name,
}: IFieldProps<IDateFieldSchema>): JSX.Element => {
  const {
    isRequired,
    label,
    placeholder,
    shouldDisplay,
    styles = {},
    value,
  } = field;

  const { t } = useTranslation('form');

  const fieldStyles = useStyles<IFieldStyles>('textField', styles);

  const { watch } = useFormContext();

  const errorMessage = useErrorMessage(name, label);
  const values = watch(name);

  const isVisible = useMemo(() => {
    return shouldDisplay ? shouldDisplay(values) : true;
  }, [values, shouldDisplay]);

  return (
    <ComponentIsVisible when={isVisible}>
      <FormControl
        isInvalid={!!errorMessage}
        isRequired={isRequired}
        {...fieldStyles.control}
      >
        <ComponentIsVisible when={!!label}>
          <FormLabel
            htmlFor={name}
            marginBottom="4px"
            variant="proRikApp"
            {...fieldStyles.label}
          >
            {label}
          </FormLabel>
        </ComponentIsVisible>
        <Controller
          defaultValue={value}
          name={name}
          render={({ field }) => (
            <Input
              aria-label={field.name}
              data-testid={id}
              name={field.name}
              onChange={field.onChange}
              placeholder={placeholder}
              sx={{
                color: 'heading',
                fontWeight: 'medium',
                fontSize: '1rem',
              }}
              type="date"
              value={field.value}
              {...fieldStyles.input}
            />
          )}
        />
        <ComponentIsVisible when={!!errorMessage}>
          <FormErrorMessage
            justifyContent="flex-end"
            textAlign="right"
            {...fieldStyles.errorMessage}
          >
            {t(`validations.${errorMessage as string}`)}
          </FormErrorMessage>
        </ComponentIsVisible>
      </FormControl>
    </ComponentIsVisible>
  );
};

export default DateField;
